import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { useOnlyEntity } from "@/hooks/use-only-entity";
import { useEffectiveDateRange } from "@/hooks/use-effective-date-range";
import { keepPreviousData } from "@tanstack/react-query";
import { useGetEntitiesInfinite } from "@/requests/gen/react-query/entity";
import { useGetDoctors } from "@/requests/gen/react-query/doctor";
import { useGetCallingAgents } from "@/requests/gen/react-query/patient360-analytics";
import {
  useAnalyticsFilters,
  DashboardType,
} from "@/hooks/use-analytics-filters";
import { Header } from "@/components/universal/header";
import { Crumb } from "@/types/index";
import { FormCombobox } from "@/components/ui/form-combobox";
import {
  ClipboardList,
  DollarSign,
  MessagesSquare,
  Package,
  PhoneCall,
} from "lucide-react";
import { useAuth } from "@/auth/use-auth";

// Dashboard Components
import { OrdersMetrics } from "./-orders/metric-cards";
import { FinanceMetrics } from "./-finance/metric-cards";
import { TextingMetrics } from "./-texting/metric-cards";
import { CallingMetrics } from "./-calling/metric-cards";
import PNLSummaryTable from "./-pnl/pnl-summary";
import RevenueBreakdownTable from "./-pnl/revenue";
import QuantityBreakdownTable from "./-pnl/quantity";
import CogsBreakdownTable from "./-pnl/cogs";
import OtherExpenseBreakdownTable from "./-pnl/other-expenses";
import CumulativeChart from "./-common/cumulative-chart";
import YTDComparisonChart from "./-common/ytd-comparison";
import UserBarChart from "./-common/user-bar-chart";
import SankeyDistribution from "./-common/sankey-distribution";
import DoctorBreakdownTable from "./-finance/doctor-breakdown-table";
import TextsSentOverTime from "./-texting/texts-sent-over-time";
import PatientCampaigns from "./-texting/campaign-enrollments";
import OptInStatus from "./-texting/opt-in-status";
import CallsOverTime from "./-calling/calls-over-time";
import MissedCallsOverTime from "./-calling/inbound-calls-over-time";
import MissedCallBreakdown from "./-calling/missed-call-breakdown";
import CallDurationHistogram from "./-calling/call-duration-histogram";
import CallsByHour from "./-calling/calls-by-hour";

// Filter Components
import { DropdownFilter } from "@/components/ui/dropdown-filter";
import {
  ParamClearButton,
  ParamDateFilter,
  ParamDatePagination,
  ParamDataTableToolbar,
  getDefaultPaginationRange,
} from "@/components/ui/param-data-table";
import TimePeriodDropdown from "@/components/ui/time-period-dropdown";
import { AssistantFilterBar } from "@/components/universal/filter-assistant/filter-bar";
import {
  lookupCurrentValue,
  lookupField,
} from "@/components/universal/filter-assistant/fields";
import {
  dateRangeWithGroupingField,
  entityLookupField,
  productTypesField,
  timePeriodField,
} from "@/components/universal/filter-assistant/presets";

// Utils
import {
  GetOrdersPerMonthParams,
  PermissionEnum,
  ProductTypeEnum,
} from "@/requests/gen/react-query/fastAPI.schemas";
import { getAutoTimePeriod } from "@/lib/time-period";
import { getThisMonth } from "@/lib/get-date-ranges";
import { RequirePermission } from "@/components/universal/require-permission";

// `tab` is the only param read off the typed search (for breadcrumb links);
// the page's other filters (date ranges, ids, etc.) are managed by nuqs, so
// passthrough keeps them from being stripped on navigation.
const searchSchema = z.object({ tab: z.string().optional() }).passthrough();

const PNL_MONTHS_IN_RANGE = 6;

export const Route = createFileRoute(
  "/_private/_patient-360/patient360-analytics/",
)({
  validateSearch: searchSchema,
  component: AnalyticsPage,
});

function AnalyticsPage() {
  const filters = useAnalyticsFilters();
  const { authUser } = useAuth();
  const onlyEntity = useOnlyEntity();

  const dashboardOptions = [
    { label: "Orders", value: DashboardType.ORDERS, icon: Package },
    { label: "Finance", value: DashboardType.FINANCE, icon: DollarSign },
    { label: "P&L Statement", value: DashboardType.PNL, icon: ClipboardList },
    { label: "Texting", value: DashboardType.TEXTING, icon: MessagesSquare },
    { label: "Calling", value: DashboardType.CALLING, icon: PhoneCall },
  ];

  const pagePermissions: Record<DashboardType, PermissionEnum> = {
    [DashboardType.ORDERS]: PermissionEnum.patient_360_analytics,
    [DashboardType.FINANCE]: PermissionEnum.entity_finance,
    [DashboardType.PNL]: PermissionEnum.entity_finance,
    [DashboardType.TEXTING]: PermissionEnum.patient_360_analytics,
    [DashboardType.CALLING]: PermissionEnum.patient_360_analytics,
  };

  // Filter available options based on permissions
  const availableOptions = dashboardOptions.filter((option) =>
    authUser?.permissions.includes(pagePermissions[option.value]),
  );

  const comboboxOptions = [
    {
      group: "Analytics Dashboards",
      items: availableOptions.map((option) => ({
        label: option.label,
        value: option.value,
        icon: option.icon,
      })),
    },
  ];

  // Resolve to a dashboard the user can actually see (e.g. a finance-only user
  // should not land on the orders tab).
  const currentDashboard =
    availableOptions.find((o) => o.value === filters.currentDashboard)?.value ??
    availableOptions[0]?.value ??
    filters.currentDashboard;

  const handleDashboardChange = (value: string | number | undefined) => {
    if (typeof value === "string") {
      filters.setCurrentDashboard(value as DashboardType);
    }
  };

  const practiceIds = onlyEntity ? [onlyEntity] : filters.practiceIds;
  const usePaginatedDate = currentDashboard === DashboardType.PNL;

  const paginatedDefaultRange = getDefaultPaginationRange(PNL_MONTHS_IN_RANGE);
  const effectivePaginatedDateStart =
    filters.queryParams.pnl_date_start ?? paginatedDefaultRange.start;
  const effectivePaginatedDateEnd =
    filters.queryParams.pnl_date_end ?? paginatedDefaultRange.end;

  const {
    effectiveDateStart,
    effectiveDateEnd,
    effectiveTimePeriod,
    markDateFilterTouched,
  } = useEffectiveDateRange({
    dateStart: filters.dateStart,
    dateEnd: filters.dateEnd,
    timePeriod: filters.timePeriod,
  });

  // API parameters
  const params: GetOrdersPerMonthParams = {
    practice_ids: practiceIds,
    doctor_ids: filters.doctorIds,
    agent_ids: filters.agentIds,
    date_start: usePaginatedDate
      ? effectivePaginatedDateStart
      : effectiveDateStart,
    date_end: usePaginatedDate ? effectivePaginatedDateEnd : effectiveDateEnd,
    product_type: filters.productTypes,
    time_period: effectiveTimePeriod,
  };

  const practices = useGetEntitiesInfinite(
    {
      search: filters.practiceSearch,
      include_ids: practiceIds,
      entity_types: ["Practice"],
      services: ["patient_360"],
    },
    {
      query: {
        placeholderData: keepPreviousData,
        getNextPageParam: (lastPage, allPages) => {
          if (lastPage.items.length === 0) {
            return undefined;
          }
          return allPages.length + 1;
        },
      },
    },
  );

  const doctors = useGetDoctors(
    { practice_ids: practiceIds },
    {
      query: {
        enabled: practiceIds.length > 0,
      },
    },
  );

  const agents = useGetCallingAgents(
    { practice_ids: practiceIds },
    {
      query: {
        enabled: currentDashboard === DashboardType.CALLING,
      },
    },
  );

  const renderFilters = () => {
    const config = filters.getConfig(currentDashboard);

    return (
      <ParamDataTableToolbar alwaysVisibleCount={0} overflowCount={Infinity}>
        {/* Date filter */}
        {config.hasDateFilter &&
          (config.usePagination ? (
            <ParamDatePagination
              startParamName="pnl_date_start"
              endParamName="pnl_date_end"
              monthsInRange={PNL_MONTHS_IN_RANGE}
            />
          ) : (
            <ParamDateFilter
              title="Date Range"
              name="date"
              defaultValue={getThisMonth()}
              onDateChange={(dateRange) => {
                markDateFilterTouched();
                if (dateRange?.from && dateRange?.to) {
                  filters.setQueryParams({
                    time_period: getAutoTimePeriod(
                      dateRange.from,
                      dateRange.to,
                    ),
                  });
                }
              }}
            />
          ))}

        {/* Entity dropdown */}
        {config.hasEntityFilter && !onlyEntity && (
          <DropdownFilter
            title="Entity"
            options={[
              {
                items:
                  practices.data?.pages
                    .flatMap((page) => page.items)
                    .map((practice) => {
                      return {
                        label: practice.name,
                        value: practice.entity_id,
                      };
                    }) ?? [],
              },
            ]}
            values={filters.queryParams.entity_ids}
            onChange={(values) =>
              filters.setQueryParams({ entity_ids: values })
            }
            search={filters.practiceSearch}
            setSearch={filters.setPracticeSearch}
            isFetching={practices.isFetchingNextPage}
            fetchNextPage={practices.fetchNextPage}
          />
        )}

        {/* Doctor Select */}
        {config.hasDoctorFilter && practiceIds.length > 0 && (
          <DropdownFilter
            title="Doctor"
            options={[
              {
                items: (doctors.data ?? []).map((doc) => ({
                  label: doc.last_name + ", " + doc.first_name,
                  value: doc.user_id,
                })),
              },
            ]}
            values={filters.doctorIds || []}
            onChange={(values) =>
              filters.setQueryParams({ doctor_ids: values })
            }
          />
        )}

        {/* Agent filter */}
        {config.hasAgentFilter && (
          <DropdownFilter
            title="Agent"
            options={[
              {
                items: (agents.data ?? []).map((agent) => ({
                  label: `${agent.last_name}, ${agent.first_name}`,
                  value: agent.user_id,
                })),
              },
            ]}
            values={filters.agentIds ?? []}
            onChange={(values) => filters.setQueryParams({ agent_ids: values })}
          />
        )}

        {/* Product Type Select */}
        {config.hasProductTypeFilter && (
          <DropdownFilter
            title="Product Type"
            options={[
              {
                items: Object.values(ProductTypeEnum).map((productType) => {
                  return { label: productType, value: productType };
                }),
              },
            ]}
            values={filters.productTypes || []}
            onChange={(values) =>
              filters.setQueryParams({ product_types: values })
            }
          />
        )}

        {/* Time Period filter */}
        {config.hasTimePeriodFilter && (
          <TimePeriodDropdown
            value={effectiveTimePeriod}
            onChange={(time_period) =>
              filters.setQueryParams({
                time_period,
              })
            }
          />
        )}

        {/* P&L has no AssistantFilterBar, so it needs its own clear button */}
        {config.usePagination && <ParamClearButton preserveTab={true} />}
      </ParamDataTableToolbar>
    );
  };

  const renderMetrics = () => {
    switch (currentDashboard) {
      case DashboardType.ORDERS:
        return <OrdersMetrics params={params} />;
      case DashboardType.FINANCE:
        return <FinanceMetrics params={params} />;
      case DashboardType.TEXTING:
        return <TextingMetrics params={params} />;
      case DashboardType.CALLING:
        return <CallingMetrics params={params} />;
      case DashboardType.PNL:
      default:
        return null;
    }
  };

  const renderContent = () => {
    switch (currentDashboard) {
      case DashboardType.ORDERS:
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <YTDComparisonChart params={params} mode={currentDashboard} />
              <CumulativeChart params={params} mode={currentDashboard} />
            </div>

            <div className="w-full">
              <SankeyDistribution params={params} mode={currentDashboard} />
            </div>

            <div className="w-full">
              <UserBarChart params={params} mode={currentDashboard} />
            </div>
          </div>
        );

      case DashboardType.FINANCE:
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <YTDComparisonChart params={params} mode={currentDashboard} />
              <CumulativeChart params={params} mode={currentDashboard} />
            </div>

            <div className="w-full">
              <SankeyDistribution params={params} mode={currentDashboard} />
            </div>

            <div className="w-full">
              <UserBarChart params={params} mode={currentDashboard} />
            </div>

            <div className="w-full">
              <DoctorBreakdownTable params={params} />
            </div>
          </div>
        );

      case DashboardType.PNL:
        return (
          <>
            <div className="w-full mb-4">
              <PNLSummaryTable params={params} />
            </div>
            <div className="w-full mb-4">
              <QuantityBreakdownTable params={params} />
            </div>
            <div className="w-full mb-4">
              <RevenueBreakdownTable params={params} />
            </div>
            <div className="w-full mb-4">
              <CogsBreakdownTable params={params} />
            </div>
            <div className="w-full mb-4">
              <OtherExpenseBreakdownTable params={params} />
            </div>
          </>
        );

      case DashboardType.TEXTING:
        return (
          <div className="space-y-4">
            <TextsSentOverTime params={params} />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <OptInStatus params={params} />
              <PatientCampaigns params={params} />
            </div>
          </div>
        );

      case DashboardType.CALLING:
        return (
          <div className="space-y-4">
            <CallsOverTime params={params} />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <MissedCallsOverTime params={params} />
              <MissedCallBreakdown params={params} />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <CallDurationHistogram params={params} />
              <CallsByHour params={params} />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Conditionally wrap content with permission requirement for finance/pnl
  const contentWithPermission = () => {
    const requiredPermission =
      currentDashboard === DashboardType.FINANCE ||
      currentDashboard === DashboardType.PNL
        ? "entity_finance"
        : "patient_360_analytics";

    return (
      <RequirePermission permission={requiredPermission}>
        {contentInner()}
      </RequirePermission>
    );
  };

  const buildAssistantFields = () => {
    const config = filters.getConfig(currentDashboard);

    return [
      ...(config.hasDateFilter
        ? [
            dateRangeWithGroupingField({
              name: "date_range",
              description: "The date range of activity to show.",
              current: config.usePagination
                ? {
                    start: effectivePaginatedDateStart,
                    end: effectivePaginatedDateEnd,
                  }
                : { start: effectiveDateStart, end: effectiveDateEnd },
              onApply: ({ start, end }, autoTimePeriod) =>
                filters.setQueryParams({
                  date_start: start,
                  date_end: end,
                  ...(config.hasTimePeriodFilter && {
                    time_period: autoTimePeriod,
                  }),
                }),
            }),
          ]
        : []),
      ...(config.hasEntityFilter && !onlyEntity
        ? [
            entityLookupField({
              name: "entity_ids",
              entityType: "Practice",
              summary: "The practice(s) whose data is shown",
              currentIds: filters.queryParams.entity_ids,
              getLabel: (id) =>
                practices.data?.pages
                  .flatMap((p) => p.items)
                  .find((p) => p.entity_id === id)?.name,
              onApply: (ids) => filters.setQueryParams({ entity_ids: ids }),
            }),
          ]
        : []),
      ...(config.hasDoctorFilter
        ? [
            lookupField({
              name: "doctor_ids",
              description:
                "The doctor(s) whose data is shown. Doctors belong to a practice: use the doctor tool with practice_ids set to the selected practice entity ids to list that practice's doctors, then return the matching doctor user_id(s). A doctor can only be selected if their practice is also selected in the practice/entity filter; if it is not, resolve and add that practice too. If no practice is selected or determinable, leave this unset.",
              current: lookupCurrentValue(filters.doctorIds, (id) => {
                const doctor = doctors.data?.find((d) => d.user_id === id);
                return doctor
                  ? `${doctor.last_name}, ${doctor.first_name}`
                  : undefined;
              }),
              onApply: (ids) => filters.setQueryParams({ doctor_ids: ids }),
            }),
          ]
        : []),
      // The agent list is small and already loaded on the calling tab, so the
      // candidates are embedded in the description instead of a lookup tool.
      ...(config.hasAgentFilter && agents.data?.length
        ? [
            lookupField({
              name: "agent_ids",
              description:
                "The calling agent(s) whose calls are shown; an empty selection means all agents. " +
                `The only valid agents are: ${agents.data
                  .map((a) => `${a.last_name}, ${a.first_name} (${a.user_id})`)
                  .join("; ")}. ` +
                "Return the user_id shown in parentheses for each agent the user names; if a name matches none of them, leave this filter unset.",
              current: lookupCurrentValue(filters.agentIds, (id) => {
                const agent = agents.data?.find((a) => a.user_id === id);
                return agent
                  ? `${agent.last_name}, ${agent.first_name}`
                  : undefined;
              }),
              onApply: (ids) => filters.setQueryParams({ agent_ids: ids }),
            }),
          ]
        : []),
      ...(config.hasProductTypeFilter
        ? [
            productTypesField({
              current: filters.productTypes,
              onApply: (product_types) =>
                filters.setQueryParams({ product_types }),
            }),
          ]
        : []),
      ...(config.hasTimePeriodFilter
        ? [
            timePeriodField({
              current: effectiveTimePeriod,
              onApply: (time_period) => filters.setQueryParams({ time_period }),
            }),
          ]
        : []),
    ];
  };

  const contentInner = () => {
    const metrics = renderMetrics();
    const config = filters.getConfig(currentDashboard);

    return (
      <>
        {/* The collapsible filter bar only exists to make room for the AI
            input; P&L's assistant cannot set the paginated date window, so it
            keeps the plain always-visible toolbar instead. */}
        {config.usePagination ? (
          renderFilters()
        ) : (
          <AssistantFilterBar fields={buildAssistantFields()}>
            {renderFilters()}
          </AssistantFilterBar>
        )}

        {/* Metric cards */}
        {metrics && (
          <div className={`mb-4 grid grid-cols-4 gap-4 lg:flex lg:flex-wrap`}>
            {metrics}
          </div>
        )}

        {/* Charts/Tables */}
        {renderContent()}
      </>
    );
  };
  const crumbs: Record<DashboardType, Crumb[]> = {
    [DashboardType.ORDERS]: [
      {
        name: "Orders Analytics",
        link: { to: "/patient360-analytics", search: { tab: "orders" } },
      },
    ],
    [DashboardType.FINANCE]: [
      {
        name: "Finance Analytics",
        link: { to: "/patient360-analytics", search: { tab: "finance" } },
      },
    ],
    [DashboardType.PNL]: [
      {
        name: "P&L Statement Analytics",
        link: { to: "/patient360-analytics", search: { tab: "pnl" } },
      },
    ],
    [DashboardType.CALLING]: [
      {
        name: "Calling Analytics",
        link: { to: "/patient360-analytics", search: { tab: "calling" } },
      },
    ],
    [DashboardType.TEXTING]: [
      {
        name: "Texting Analytics",
        link: { to: "/patient360-analytics", search: { tab: "texting" } },
      },
    ],
  };

  // RENDER PAGE
  return (
    <div className="pb-4">
      <title>Patient 360 Analytics - Legrande Health Portal</title>
      <Header
        crumbs={crumbs[currentDashboard]}
        children={
          <FormCombobox
            options={comboboxOptions}
            value={currentDashboard}
            onChange={handleDashboardChange}
            className="w-full sm:w-44"
          />
        }
      />

      {contentWithPermission()}
    </div>
  );
}
